import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    Checkbox,
    Collapse,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Typography,
    CircularProgress,
    useTheme
} from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import { IconSearch, IconFilter, IconFilterOff, IconPlus, IconAlertTriangle } from '@tabler/icons';
import api from '../../services/api';
import TicketDetailDrawer from './TicketDetailDrawer';
import TicketFormDrawer from './TicketFormDrawer';
import { PRIORITY_COLORS, PRIORITY_LABELS, STATUS_COLORS, STATUS_LABELS, STATUS_ORDER, formatTicketDate, shortTicketId } from './ticketUtils';

const FILTROS_INICIAIS = { priority: '', team: '', assignedTo: '', createdAfter: '', createdBefore: '', overdue: false };

// Indicador numérico simples (mesmo espírito do StatCard de
// DashboardDeals.jsx, só que local/sem gráfico) - usado na barra de
// métricas no topo da listagem.
const MetricBox = ({ label, value, color, isDark }) => (
    <Paper
        sx={{
            p: 1.5,
            borderRadius: '14px',
            minWidth: 110,
            flex: 1,
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)'
        }}
    >
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: color || 'inherit' }}>
            {value}
        </Typography>
    </Paper>
);

function debounce(func, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

const TicketsPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const location = useLocation();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [teams, setTeams] = useState([]);
    const [metrics, setMetrics] = useState(null);

    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);

    const [orderBy, setOrderBy] = useState('updated_at');
    const [orderDir, setOrderDir] = useState('desc');

    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [openCreate, setOpenCreate] = useState(false);

    // Deep-link (?conversation=<id> / ?deal=<id>) - vindo de "Ver todos" a
    // partir da Conversation/Deal.
    const params = new URLSearchParams(location.search);
    const linkConversation = params.get('conversation');
    const linkDeal = params.get('deal');

    // Deep-link (?id=<ticketId>) - vindo do link de uma notificação (ex:
    // "Ticket atribuído a você"), mesmo padrão já usado em Chats3.js pra
    // abrir uma conversa específica direto da URL.
    const linkTicketId = params.get('id');
    const didOpenTicketFromQuery = useRef(false);

    const pegarAppPadrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegarAppPadrao();
    }, []);

    useEffect(() => {
        if (!appId || !linkTicketId || didOpenTicketFromQuery.current) return;
        didOpenTicketFromQuery.current = true;
        setSelectedTicketId(linkTicketId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, linkTicketId]);

    useEffect(() => {
        if (!appId) return;
        api
            .get(`v1/apps/${appId}/teams/`)
            .then((response) => setTeams(response.data?.teams || []))
            .catch((error) => console.error('Erro ao carregar equipes', error));
    }, [appId]);

    const loadMetrics = async () => {
        if (!appId) return;
        try {
            const response = await api.get(`v1/apps/${appId}/tickets/metrics/`);
            setMetrics(response.data);
        } catch (error) {
            console.error('Erro ao carregar métricas de tickets', error);
        }
    };

    const loadTickets = async () => {
        if (!appId) return;
        setLoading(true);
        try {
            const query = new URLSearchParams();
            if (searchTerm) query.set('search', searchTerm);
            if (filtros.priority) query.set('priority', filtros.priority);
            if (filtros.team) query.set('team', filtros.team);
            if (filtros.assignedTo) query.set('assigned_to', filtros.assignedTo);
            if (filtros.createdAfter) query.set('created_after', filtros.createdAfter);
            if (filtros.createdBefore) query.set('created_before', filtros.createdBefore);
            if (filtros.overdue) query.set('overdue', 'true');
            if (linkConversation) query.set('conversation', linkConversation);
            if (linkDeal) query.set('deal', linkDeal);

            const response = await api.get(`v1/apps/${appId}/tickets/?${query.toString()}`);
            setTickets(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar tickets', error);
            setStatus({ type: 'error', message: 'Erro ao carregar Tickets.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, searchTerm, filtros, linkConversation, linkDeal]);

    useEffect(() => {
        loadMetrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const handleSearchChange = useMemo(
        () =>
            debounce((value) => {
                setSearchTerm(value);
            }, 400),
        []
    );

    const [searchInput, setSearchInput] = useState('');
    const onSearchInputChange = (e) => {
        setSearchInput(e.target.value);
        handleSearchChange(e.target.value);
    };

    const limparFiltros = () => setFiltros(FILTROS_INICIAIS);
    const limparDeepLink = () => history.push('/dashboard/config/tickets');
    const filtrosAtivosCount = Object.values(filtros).filter((v) => v !== '' && v !== false).length;

    const counts = useMemo(() => {
        const map = { all: tickets.length };
        STATUS_ORDER.forEach((s) => {
            map[s] = tickets.filter((t) => t.status === s).length;
        });
        return map;
    }, [tickets]);

    const ticketsForTab = useMemo(() => {
        const base = activeTab === 'all' ? tickets : tickets.filter((t) => t.status === activeTab);
        const sorted = [...base].sort((a, b) => {
            let av = a[orderBy];
            let bv = b[orderBy];
            if (orderBy === 'priority') {
                const order = { low: 0, normal: 1, high: 2, urgent: 3 };
                av = order[av] ?? 0;
                bv = order[bv] ?? 0;
            } else {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
            }
            return orderDir === 'asc' ? av - bv : bv - av;
        });
        return sorted;
    }, [tickets, activeTab, orderBy, orderDir]);

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrderDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            setOrderDir('desc');
        }
    };

    const displayName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email : null);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h2">Tickets</Typography>
                <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={() => setOpenCreate(true)} sx={{ textTransform: 'none' }}>
                    Novo Ticket
                </Button>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {metrics && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                    <MetricBox label="Total" value={metrics.total} isDark={isDark} />
                    <MetricBox label="Pendentes" value={metrics.pending} isDark={isDark} />
                    <MetricBox label="Vencidos" value={metrics.overdue} color={metrics.overdue > 0 ? '#B42121' : undefined} isDark={isDark} />
                    <MetricBox label="Reabertos" value={metrics.reopened} isDark={isDark} />
                    <MetricBox
                        label="Tempo médio de resolução"
                        value={
                            metrics.avg_resolution_seconds
                                ? `${(metrics.avg_resolution_seconds / 3600).toFixed(1)}h`
                                : '—'
                        }
                        isDark={isDark}
                    />
                </Box>
            )}

            {(linkConversation || linkDeal) && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={limparDeepLink}>
                    Mostrando só os tickets {linkConversation ? 'desta conversa' : 'deste negócio'}.
                </Alert>
            )}

            <OutlinedInput
                fullWidth
                placeholder="Buscar por título ou descrição..."
                value={searchInput}
                onChange={onSearchInputChange}
                sx={{ mb: 2, maxWidth: 480, borderRadius: '14px' }}
                startAdornment={
                    <InputAdornment position="start">
                        <IconSearch size="1.2rem" color="#999" />
                    </InputAdornment>
                }
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Button
                    variant="outlined"
                    startIcon={<IconFilter size={18} />}
                    onClick={() => setShowFilters((prev) => !prev)}
                    sx={{ borderRadius: '14px', textTransform: 'none' }}
                >
                    <Badge badgeContent={filtrosAtivosCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -10 } }}>
                        Filtros
                    </Badge>
                </Button>
                {filtrosAtivosCount > 0 && (
                    <Button variant="text" startIcon={<IconFilterOff size={18} />} onClick={limparFiltros} sx={{ textTransform: 'none' }}>
                        Limpar filtros
                    </Button>
                )}
            </Box>

            <Collapse in={showFilters}>
                <Paper sx={{ p: 2.5, borderRadius: '20px', mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Prioridade"
                                value={filtros.priority}
                                onChange={(e) => setFiltros({ ...filtros, priority: e.target.value })}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Equipe"
                                value={filtros.team}
                                onChange={(e) => setFiltros({ ...filtros, team: e.target.value })}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="mine">Minha equipe</MenuItem>
                                {teams.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Criado a partir de"
                                InputLabelProps={{ shrink: true }}
                                value={filtros.createdAfter}
                                onChange={(e) => setFiltros({ ...filtros, createdAfter: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Criado até"
                                InputLabelProps={{ shrink: true }}
                                value={filtros.createdBefore}
                                onChange={(e) => setFiltros({ ...filtros, createdBefore: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ display: 'flex', gap: 3 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtros.assignedTo === 'me'}
                                        onChange={(e) => setFiltros({ ...filtros, assignedTo: e.target.checked ? 'me' : '' })}
                                    />
                                }
                                label="Meus tickets"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtros.assignedTo === 'none'}
                                        onChange={(e) => setFiltros({ ...filtros, assignedTo: e.target.checked ? 'none' : '' })}
                                    />
                                }
                                label="Sem responsável"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtros.overdue}
                                        onChange={(e) => setFiltros({ ...filtros, overdue: e.target.checked })}
                                    />
                                }
                                label="Vencidos"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ borderBottom: `1px solid ${isDark ? '#2a3942' : '#e0e0e0'}`, mb: 1 }}
            >
                <Tab
                    value="all"
                    label={
                        <Badge badgeContent={counts.all} color="secondary" max={999}>
                            <Typography variant="body2" sx={{ pr: counts.all ? 1 : 0 }}>
                                Todos
                            </Typography>
                        </Badge>
                    }
                />
                {STATUS_ORDER.map((s) => (
                    <Tab
                        key={s}
                        value={s}
                        label={
                            <Badge badgeContent={counts[s]} color="secondary" max={999}>
                                <Typography variant="body2" sx={{ pr: counts[s] ? 1 : 0 }}>
                                    {STATUS_LABELS[s]}
                                </Typography>
                            </Badge>
                        }
                    />
                ))}
            </Tabs>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : ticketsForTab.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h5" sx={{ opacity: 0.4, fontWeight: 700 }}>
                        Nenhum Ticket encontrado.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: '16px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Título</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    <TableSortLabel active={orderBy === 'priority'} direction={orderDir} onClick={() => handleSort('priority')}>
                                        Prioridade
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Equipe</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Responsável</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    <TableSortLabel active={orderBy === 'updated_at'} direction={orderDir} onClick={() => handleSort('updated_at')}>
                                        Atualizado em
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ticketsForTab.map((t) => (
                                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedTicketId(t.id)}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t.title}
                                            </Typography>
                                            {t.is_overdue && (
                                                <Chip
                                                    icon={<IconAlertTriangle size={13} />}
                                                    label="Vencido"
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FBD5D5', color: '#B42121' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{ opacity: 0.6, fontFamily: 'monospace' }}>
                                            {shortTicketId(t.id)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={STATUS_LABELS[t.status] || t.status}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                bgcolor: STATUS_COLORS[t.status]?.bg,
                                                color: STATUS_COLORS[t.status]?.color
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PRIORITY_LABELS[t.priority] || t.priority}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                bgcolor: PRIORITY_COLORS[t.priority]?.bg,
                                                color: PRIORITY_COLORS[t.priority]?.color
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{t.team_detail?.name || '—'}</TableCell>
                                    <TableCell>{displayName(t.assigned_to_detail) || '—'}</TableCell>
                                    <TableCell>{displayName(t.contact_detail) || '—'}</TableCell>
                                    <TableCell>{formatTicketDate(t.updated_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedTicketId && (
                <TicketDetailDrawer
                    appId={appId}
                    ticketId={selectedTicketId}
                    onClose={() => {
                        setSelectedTicketId(null);
                        if (linkTicketId) {
                            const next = new URLSearchParams(location.search);
                            next.delete('id');
                            history.replace(`/dashboard/config/tickets${next.toString() ? `?${next.toString()}` : ''}`);
                        }
                    }}
                    onChanged={() => {
                        loadTickets();
                        loadMetrics();
                    }}
                />
            )}

            {openCreate && (
                <TicketFormDrawer
                    appId={appId}
                    initialConversationId={linkConversation}
                    initialDealId={linkDeal}
                    onClose={() => setOpenCreate(false)}
                    onSaved={() => {
                        setOpenCreate(false);
                        loadTickets();
                        loadMetrics();
                    }}
                />
            )}
        </Box>
    );
};

export default TicketsPage;
